import * as React from 'react';
import type { ToolProps } from './Tool';
import { Tool } from './Tool';
import { useToggleLayer } from 'react-laag';
import { DEFAULT_PLACEMENT_CONFIG, reactNodeIsOfType } from '../../util';
import type { ChildrenProp } from '../common-types';
import { intl } from '../../lib';
import type { Container } from 'react-laag/dist/ToggleLayer/types';
import FocusTrap from 'focus-trap-react';

/**
 * Props for a {@link MenuTool}.
 */
export type MenuToolProps = Omit<ToolProps, 'onClick' | 'icon'> &
  Partial<Pick<ToolProps, 'icon'>> &
  Required<ChildrenProp>;

/**
 * A Tool (button) that shows a menu containing the given tools when clicking on it.
 * The tools are passed as children and the MenuTool will throw an exception when trying to display
 * an invalid child.
 *
 * @param properties {@link MenuToolProps} object
 * @return the created {@link JSX.Element}
 * @constructor
 */
export function MenuTool({
  icon = 'menu',
  children,
  containerElement,
  tooltipOptions = {
    // defaulted to immediately hide the tooltip without any delay
    // to prevent the tooltip from appearing above the shown menu when opening it
    delayLeave: 0
  },
  ...props
}: Readonly<
  MenuToolProps & {
    containerElement?: () => HTMLElement | null;
  }
>): JSX.Element {
  // hook config for the shown menu
  const [menuElement, toggleLayerProps] = useToggleLayer(
    // determine how to render the layer
    ({ isOpen, layerProps }) =>
      isOpen && (
        <FocusTrap
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: () => {
              toggleLayerProps.close();
              return true;
            },
            preventScroll: true,
            initialFocus: false
          }}
        >
          <div
            className="ehs-menu"
            {...layerProps}
            style={{ ...layerProps.style }}
            ref={layerProps.ref}
          >
            {React.Children.map(children, child => {
              if (!React.isValidElement(child)) {
                return null;
              }

              // make sure the properties we need exist on the child
              if (!child.props.onClick) {
                // needs to be caught by a developer
                throw new Error(
                  'MenuTool is expecting its children to have an onClick property.'
                );
              }

              // override tool click handlers so that they also close the menu
              const oldClickHandler = child.props.onClick;
              const newClickHandler = (event: React.MouseEvent): void => {
                toggleLayerProps.close();
                oldClickHandler(event);
              };

              // props that will be passed to the cloned element
              const clonedElementProps = {
                ...child.props,
                className: ['ehs-menu-child', child.props.className]
                  .filter(Boolean)
                  .join(' '),
                onClick: newClickHandler
              };

              // some special handling for if the child elements are `Tool`s
              if (reactNodeIsOfType(child, Tool)) {
                // use icon as fallback for the key, since the same icon shouldn't be repeated within the menu
                clonedElementProps.key = child.key ?? child.props.icon;
                // set the tool text to either use the defined 'text' or 'tooltip' property if one was passed
                clonedElementProps.text =
                  child.props.text ?? child.props.tooltip;
                // remove the tooltip, since a tool inside a MenuTool having a tooltip doesn't really make sense
                clonedElementProps.tooltip = undefined;
              }

              // return adjusted element
              return React.cloneElement(child, clonedElementProps);
            })}
          </div>
        </FocusTrap>
      ),
    // optionally provide options
    {
      placement: DEFAULT_PLACEMENT_CONFIG,
      closeOnOutsideClick: true,
      container: containerElement as Container
    }
  );
  return (
    <>
      {menuElement}
      <Tool
        {...props}
        className={
          toggleLayerProps.isOpen
            ? 'ehs-menuTool-trigger active'
            : 'ehs-menuTool-trigger'
        }
        icon={icon}
        onClick={
          toggleLayerProps.isOpen
            ? toggleLayerProps.close
            : toggleLayerProps.openFromMouseEvent
        }
        tooltip={props.tooltip ?? intl('view.button.show_additional_tools')}
        tooltipOptions={tooltipOptions}
        aria-expanded={toggleLayerProps.isOpen}
      />
    </>
  );
}
